import { useMemo, useState } from 'react';
import { Wrapper, Header, Footer } from '../../general_components/forms/Form';
import { IosToggle } from '../../general_components/content_management/reusableItems/iosToggle';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { addNotification } from '../../supplementary_components/store';
import {
    buildReportColumnAccessOpts,
    getVisibleReportColumnCatalog,
    normalizeReportColumns,
    persistReportCustomizations,
    retrieveReportCustomizations,
    updateLocalReportColumns,
} from './reportColumnSettings';
import styles from './reportColumnSettingsPopup.module.scss';

const EMPTY_CATALOG = [];

const popupSections = (popupData) => {
    if (Array.isArray(popupData?.sections) && popupData.sections.length > 0) {
        return popupData.sections;
    }
    return [{
        reportKey: popupData?.reportKey,
        catalog: popupData?.catalog,
        visibleColumns: popupData?.visibleColumns,
        sectionTitle: popupData?.sectionTitle,
        sectionHelp: popupData?.sectionHelp,
        onSaved: popupData?.onSaved,
    }];
};

export const ReportColumnSettingsPopup = ({ closePopup, popupData }) => {
    const sections = popupSections(popupData);
    const accessOpts = useMemo(() => buildReportColumnAccessOpts(), []);
    const catalogs = useMemo(() => {
        const byKey = {};
        sections.forEach((section) => {
            byKey[section.reportKey] = getVisibleReportColumnCatalog(
                Array.isArray(section.catalog) ? section.catalog : EMPTY_CATALOG,
                accessOpts
            );
        });
        return byKey;
    }, [sections, accessOpts]);

    const [selectedByKey, setSelectedByKey] = useState(() => {
        const stored = retrieveReportCustomizations();
        const initial = {};
        sections.forEach((section) => {
            initial[section.reportKey] = normalizeReportColumns(
                section.visibleColumns ?? stored[section.reportKey],
                section.catalog,
                accessOpts
            );
        });
        return initial;
    });
    const [saving, setSaving] = useState(false);

    const toggleColumn = (section, key, locked) => {
        if (locked) return;
        setSelectedByKey((prev) => {
            const current = prev[section.reportKey] || [];
            const next = current.includes(key)
                ? current.filter((item) => item !== key)
                : [...current, key];
            return {
                ...prev,
                [section.reportKey]: normalizeReportColumns(next, section.catalog, accessOpts),
            };
        });
    };

    const onSave = () => {
        if (saving || sections.some((section) => !section.reportKey)) return;
        const saved = {};
        const body = {};
        sections.forEach((section) => {
            const columns = normalizeReportColumns(selectedByKey[section.reportKey], section.catalog, accessOpts);
            saved[section.reportKey] = columns;
            body[section.reportKey] = columns;
        });
        setSaving(true);
        sendRequest(
            'PATCH',
            API_ROOT + 'employees/reportCustomizations',
            null,
            (updated) => {
                if (updated && typeof updated === 'object') {
                    persistReportCustomizations({
                        ...retrieveReportCustomizations(),
                        ...updated,
                    });
                }
                sections.forEach((section) => {
                    const next = updateLocalReportColumns(section.reportKey, saved[section.reportKey], section.catalog, accessOpts);
                    section.onSaved?.(next);
                });
                setSaving(false);
                addNotification({ message: 'Report settings saved', type: 'success' });
                closePopup();
            },
            () => {
                setSaving(false);
                addNotification({ message: 'Failed to save report settings', type: 'alert' });
            },
            body
        );
    };

    return (
        <Wrapper>
            <Header>
                <span>Report Settings</span>
            </Header>
            <div className={styles.content}>
                {sections.map((section) => (
                    <section key={section.reportKey} className={styles.section}>
                        <h3 className={styles.sectionTitle}>{section.sectionTitle || 'Load columns'}</h3>
                        <p className={styles.sectionHelp}>
                            {section.sectionHelp || 'Choose which fields appear when a dispatcher row is expanded. Load # and Rate cannot be removed.'}
                        </p>
                        <div className={styles.columnList}>
                            {(catalogs[section.reportKey] || []).map((col) => {
                                const on = (selectedByKey[section.reportKey] || []).includes(col.key);
                                return (
                                    <div key={col.key} className={styles.row}>
                                        <span className={styles.rowLabel}>{col.label}</span>
                                        <IosToggle
                                            state={on}
                                            disabled={col.locked}
                                            onChangeAction={() => toggleColumn(section, col.key, col.locked)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                ))}
            </div>
            <Footer>
                <div className="non-selectable" onClick={() => !saving && closePopup()}>
                    Close
                </div>
                <div
                    className={`non-selectable ${saving ? 'disabled' : ''}`}
                    onClick={onSave}
                >
                    <input type="button" value={saving ? 'Saving…' : 'Save'} readOnly disabled={saving} />
                </div>
            </Footer>
        </Wrapper>
    );
};
